import { Directive, ElementRef, Input, OnInit } from '@angular/core';
import { AUTO_STYLE, keyframes, style } from '@angular/animations';

import { NgStaticAnimationService } from '../../shared/services/static-animation.service';
import { EnumNgEventName } from '../../shared/event-animation.data';
import { INgAnimationStates, INgAnimationTiming, INgEffectOptions } from '../../shared/event-animation.interface';


/**------------------------------------------------------
 * Animation State Definitions
 */
function sink(translateY: number | undefined): INgAnimationStates {

	//0 - set the default if no specific value is given
	translateY = translateY ? translateY : 8;

	//1 - create the animations state
	const animationStates: INgAnimationStates = {
		start 	: keyframes([
			style({
				visibility 				: AUTO_STYLE,
				transform  				: `translateY(${translateY}px)`,
				'transition-property' 	: 'transform',
				easing 					: 'ease-in',
				offset 					: 0
			}),
			style({
				transform  				: 'translateY(0)',
				'transition-property' 	: 'transform',
				easing 					: 'ease-in',
				offset 					: 1
			})
		]),
		end		: keyframes([
			style({
				visibility 				: AUTO_STYLE,
				transform  				: 'translateY(0)',
				'transition-property' 	: 'transform',
				easing 					: 'ease-out',
				offset 					: 0
			}),
			style({
				transform 				: `translateY(${translateY}px)`,
				'transition-property' 	: 'transform',
				easing 					: 'ease-out',
				offset 					: 1
			})
		])
	};
	return animationStates;
}


/**------------------------------------------------------
 * Sink on Event
 */
@Directive({
    selector: '[ngSinkOnEvent]',
    providers: [NgStaticAnimationService],
    standalone: false
})
export class NgSinkOnEventDirective implements OnInit {

	//** Input Decorators */
	@Input() effectOptions: Partial<INgCustomEffectOptions> = null!;
	@Input() targetElement: HTMLElement = null!;

	constructor(
		private elementRef				: ElementRef,
		private staticAnimationService 	: NgStaticAnimationService
	) {}

	//** Setup the Animation */
	ngOnInit(): void {

		//0 - check the translateY value is relative to the animation functionality
		if (this.effectOptions?.translateY && this.effectOptions?.translateY < 0) throw new Error(`SinkOnEventDirective => ngOnInit => FATAL ERROR: Provided translateY value should be a positive number: ${this.effectOptions?.translateY}`);

		//1 - setup the animation
		this.staticAnimationService.setupAnimation({
			triggerElement	: this.elementRef.nativeElement,
			targetElement	: this.targetElement,
			states			: sink(this.effectOptions?.translateY),
			event			: this.effectOptions?.event!,
			timing			: {
				duration	: this.effectOptions?.duration,
				delay		: this.effectOptions?.delay
			}
		});
	}
}


/**------------------------------------------------------
 * Sink on Mouse Hover
 */
@Directive({
    selector: '[ngSinkOnMouseHover]',
    providers: [NgStaticAnimationService],
    standalone: false
})
export class NgSinkOnMouseHoverDirective implements OnInit {

	//** Input Decorators */
	@Input() timingOptions: Partial<INgCustomAnimationTiming> = null!;
	@Input() targetElement: HTMLElement 					= null!;

	constructor(
		private elementRef				: ElementRef,
		private staticAnimationService 	: NgStaticAnimationService
	) {}

	//** Setup the Animation */
	ngOnInit(): void {

		//0 - check the translateY value is relative to the animation functionality
		if (this.timingOptions?.translateY && this.timingOptions?.translateY < 0) throw new Error(`SinkOnMouseHoverDirective => ngOnInit => FATAL ERROR: Provided translateY value should be a positive number: ${this.timingOptions?.translateY}`);

		//1 - setup the animation
		this.staticAnimationService.setupAnimation({
			triggerElement	: this.elementRef.nativeElement,
			targetElement	: this.targetElement,
			states			: sink(this.timingOptions?.translateY),
			event			: EnumNgEventName.MouseHover,
			timing			: this.timingOptions
		});
	}
}


//** Interfaces --------------------------------- */
interface INgCustomEffectOptions extends INgEffectOptions {
	translateY : number;
}

interface INgCustomAnimationTiming extends INgAnimationTiming {
	translateY : number;
}
